import { Button, Modal } from 'antd';
import React from 'react';
import {
  modalIsacOpen,
  ModalISacType,
  toggleModalIsac,
} from '../../store/StoreContext';
import CommonButton from '../commons/buttons/CommonButton';
import CommonIcon from '../commons/CommonIcon/CommonIcon';
import Translate from '../language/Translate';

interface ModalISacProps {}

type IsacPage = Exclude<ModalISacType, false>;

/**
 * Le pagine di injection di ISac sono servite dalla webapp legacy, montata su
 * `/next` nella stessa immagine: sotto `/app` non esistono e rispondono 404
 * (era il motivo per cui la modale mostrava "Not Found"). L'host viene tolto in
 * fase di build dal Dockerfile di deploy, quindi in produzione l'iframe punta a
 * `/next/...` sullo stesso origin.
 */
const ISAC_BASE =
  'https://securedrivecl.infocert.it/next/isac/full-pages-injection';

const ISAC_URLS: Record<IsacPage, string> = {
  profile: `${ISAC_BASE}/account.html#/account`,
  passwordRecovery: `${ISAC_BASE}/password-recovery.html`,
  passwordExpired: `${ISAC_BASE}/password-renew.html`,
};

const ISAC_TITLES: Record<IsacPage, string> = {
  profile: 'Isac.title.profile',
  passwordRecovery: 'Isac.title.passwordRecovery',
  passwordExpired: 'Isac.title.passwordExpired',
};

/**
 * Cookie che ISac legge per sapere chi lo ospita e dove tornare a fine flusso.
 * Sono gli stessi che impostava la webapp legacy (`Isac.setRedirectCookies`):
 * senza, la pagina di injection non richiama il parent e la modale resta
 * aperta a operazione conclusa.
 */
const setIsacRedirectCookies = () => {
  const cookies = {
    'isac-app': 'sdrive-web',
    'isac-redirect': '_isac_redirect_callback.html?cb=closeModal',
    'isac-force-mail': '/API/securityinfo/mail',
  };
  Object.entries(cookies).forEach(([name, value]) => {
    document.cookie = `${name}=${value}; path=/`;
  });
};

/** Apre una pagina ISac in modale, impostando prima i cookie di ritorno. */
export const openIsac = (type: IsacPage) => {
  setIsacRedirectCookies();
  toggleModalIsac(type);
};

const ModalISac: React.FC<ModalISacProps> = ({}) => {
  const onClose = () => {
    toggleModalIsac(false);
  };

  // Determina URL e titolo in base al tipo
  const currentPage = modalIsacOpen.value;
  const currentUrl = currentPage ? ISAC_URLS[currentPage] : '';
  const currentTitle = currentPage ? ISAC_TITLES[currentPage] : '';

  // `_isac_redirect_callback.html?cb=closeModal` invoca `window.top.closeModal`
  // quando ISac ha finito: è così che la modale si chiude da sola.
  React.useEffect(() => {
    (window as any).closeModal = onClose;
    return () => {
      delete (window as any).closeModal;
    };
  }, []);

  return (
    <Modal
      className="modal-isac"
      open={!!currentPage}
      onCancel={onClose}
      title={currentTitle ? <Translate i18nKey={currentTitle} /> : null}
      width={800}
      footer={[
        <CommonButton
          key="close"
          icon={<CommonIcon icon="Close" />}
          onClick={onClose}
          title="globals.close"
        />,
      ]}
    >
      {currentUrl && (
        <iframe
          src={currentUrl}
          style={{
            width: '100%',
            height: '600px',
            border: 'none',
          }}
          title="ISac"
        />
      )}
    </Modal>
  );
};

export default ModalISac;

export const IsacModalButton = ({
  type = 'profile',
}: {
  type?: ModalISacType;
}) => {
  const openIsacModal = (e: React.MouseEvent) => {
    e.preventDefault();
    if (type) {
      openIsac(type);
    }
  };
  return (
    <Button
      onClick={openIsacModal}
      className="text-blue-500 !m-0 !p-0 text-left text-xs font-semibold hover:underline"
      type="link"
    >
      <Translate i18nKey={`Profile.${type}`} />
    </Button>
  );
};
